import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

// Fullscreen image lightbox with zoom + pan + keyboard nav.
// Pinch-to-zoom works natively on mobile thanks to touch-action: pinch-zoom.
export default function ImageZoomLightbox({ images, startIndex = 0, alt = "", onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, ox: 0, oy: 0 });

  const reset = useCallback(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, []);
  const next = useCallback(() => { setIndex((i) => (i + 1) % images.length); reset(); }, [images.length, reset]);
  const prev = useCallback(() => { setIndex((i) => (i - 1 + images.length) % images.length); reset(); }, [images.length, reset]);
  const zoomIn = useCallback(() => setZoom((z) => Math.min(z + 0.5, 4)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(z - 0.5, 1)), []);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && images.length > 1) next();
      else if (e.key === "ArrowLeft" && images.length > 1) prev();
      else if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-") zoomOut();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, next, prev, zoomIn, zoomOut, images.length]);

  // Mouse wheel zoom (desktop)
  const onWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn(); else zoomOut();
  };

  // Drag-to-pan when zoomed in (desktop)
  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    setDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y });
  };
  const onMouseMove = (e) => {
    if (!dragging) return;
    setOffset({ x: dragStart.ox + (e.clientX - dragStart.x), y: dragStart.oy + (e.clientY - dragStart.y) });
  };
  const onMouseUp = () => setDragging(false);

  // Toggle zoom on click (1x ↔ 2x)
  const onImageClick = (e) => {
    if (dragging) return;
    e.stopPropagation();
    setZoom((z) => (z === 1 ? 2 : 1));
    if (zoom !== 1) setOffset({ x: 0, y: 0 });
  };

  return (
    <div
      data-testid="image-lightbox"
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="lightbox-close"
        aria-label="Close"
        className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all"
      >
        <X size={22}/>
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-2 py-2">
        <button type="button" onClick={(e) => { e.stopPropagation(); zoomOut(); }} disabled={zoom <= 1}
          data-testid="lightbox-zoom-out" aria-label="Zoom out"
          className="w-10 h-10 rounded-full hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-40 transition-all">
          <ZoomOut size={18}/>
        </button>
        <span data-testid="lightbox-zoom-level" className="text-white text-xs font-bold tracking-[0.15em] min-w-[44px] text-center">{Math.round(zoom * 100)}%</span>
        <button type="button" onClick={(e) => { e.stopPropagation(); zoomIn(); }} disabled={zoom >= 4}
          data-testid="lightbox-zoom-in" aria-label="Zoom in"
          className="w-10 h-10 rounded-full hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-40 transition-all">
          <ZoomIn size={18}/>
        </button>
      </div>

      {images.length > 1 && (
        <>
          <button type="button" onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="lightbox-prev" aria-label="Previous photo"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all">
            <ChevronLeft size={24}/>
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="lightbox-next" aria-label="Next photo"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all">
            <ChevronRight size={24}/>
          </button>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 text-white text-xs font-bold tracking-[0.18em] bg-white/10 backdrop-blur rounded-full px-3 py-1.5">
            {index + 1} / {images.length}
          </div>
        </>
      )}

      <div
        className="w-full h-full flex items-center justify-center overflow-hidden"
        style={{ touchAction: "pinch-zoom" }}
        onWheel={onWheel}
      >
        <img
          src={images[index]}
          alt={alt}
          draggable={false}
          onMouseDown={onMouseDown}
          onClick={onImageClick}
          data-testid="lightbox-image"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transition: dragging ? "none" : "transform 0.2s ease",
            cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
            maxWidth: "92vw",
            maxHeight: "88vh",
            objectFit: "contain",
            userSelect: "none",
          }}
        />
      </div>
    </div>
  );
}
